import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Lock, Building2, CreditCard, Loader2 } from "lucide-react";

interface SignUpConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  gymName: string | null;
  dni: string | null;
  loading: boolean;
  onConfirm: () => void;
}

interface RowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const Row = ({ icon, label, value }: RowProps) => (
  <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
    <div className="mt-0.5 text-white/70">{icon}</div>
    <div className="min-w-0 flex-1">
      <p className="text-xs uppercase tracking-wide text-white/50">{label}</p>
      <p className="truncate text-sm font-medium text-white">{value}</p>
    </div>
  </div>
);

export const SignUpConfirmDialog = ({
  open,
  onOpenChange,
  email,
  gymName,
  dni,
  loading,
  onConfirm,
}: SignUpConfirmDialogProps) => {
  const handleOpenChange = (next: boolean) => {
    if (loading) return; // No permitir cerrar mientras se procesa el registro
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-black/70 backdrop-blur-xl border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Confirmá tus datos</DialogTitle>
          <DialogDescription className="text-white/70">
            Revisá que todo esté correcto antes de crear tu cuenta. Si hay algún error,
            podés volver y corregirlo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Row
            icon={<Mail className="h-4 w-4" />}
            label="Correo electrónico"
            value={email}
          />
          <Row
            icon={<Lock className="h-4 w-4" />}
            label="Contraseña"
            value="••••••••"
          />
          {gymName ? (
            <Row
              icon={<Building2 className="h-4 w-4" />}
              label="Gimnasio"
              value={gymName}
            />
          ) : (
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-white/60">
              Sin gimnasio asociado
            </div>
          )}
          {dni && (
            <Row
              icon={<CreditCard className="h-4 w-4" />}
              label="DNI / Documento"
              value={dni}
            />
          )}
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            Volver y editar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creando cuenta...
              </>
            ) : (
              "Confirmar y registrarme"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
